import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import AssetCardDragOverlay from "@v1/components/assets/AssetCardDragOverlay";
import Popover from "@v1/components/designSystem/popover/Popover";
import useCurrentDragType from "@v1/hooks/useCurrentDragType";
import { AssetCardActions } from "@v2/features/assets/components/asset-card/AssetCardActions";
import { AssetPreview } from "@v2/features/assets/components/asset-card/AssetPreview";
import { AssetThumbnail } from "@v2/features/assets/components/asset-card/AssetThumbnail";
import { formatAssetDisplayName } from "@v2/features/assets/utils/formatAssetDisplayName";
import { useRequiredContext } from "replo-utils/react/context";

export type V1AssetCardProps = {
  asset: Asset;
  onClick?: () => void;
  shouldAllowActions?: boolean;
  shouldAllowDrag?: boolean;
  isUploading?: boolean;
  className?: string;
};

type AssetCardAnchorProps = "onClick" | "shouldAllowDrag" | "className";

const AssetCardContext = React.createContext<{
  isAssetPreviewLoaded: boolean;
  setIsAssetPreviewLoaded: (isAssetPreviewLoaded: boolean) => void;
  asset: Asset;
  handlePopoverOpenChange: (isOpen: boolean) => void;
  anchorRef: React.RefObject<HTMLDivElement>;
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setActionPopoverContent: (
    actionPopoverContent: "delete" | "rename" | null,
  ) => void;
  actionPopoverContent: "delete" | "rename" | null;
  isUploading?: boolean;
} | null>(null);

const useAssetCardContext = () => useRequiredContext(AssetCardContext);

const AssetCardRoot: React.FC<
  React.PropsWithChildren<Omit<V1AssetCardProps, AssetCardAnchorProps>>
> = (props) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isAssetPreviewLoaded, setIsAssetPreviewLoaded] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [actionPopoverContent, setActionPopoverContent] = React.useState<
    "delete" | "rename" | null
  >(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handlePopoverOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen);
    if (!isOpen) {
      setIsAssetPreviewLoaded(false);
    }
  };

  // NOTE (Max, 2025-09-23): Close popover if anchor is not intersecting (hidden due to overflow)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Close popover when anchor element is not visible due to overflow
  React.useEffect(() => {
    if (!anchorRef.current) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry && !entry.isIntersecting && isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    });

    observer.observe(anchorRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isPopoverOpen]);

  const contextValue = {
    isAssetPreviewLoaded,
    setIsAssetPreviewLoaded,
    asset: props.asset,
    handlePopoverOpenChange,
    anchorRef,
    isMenuOpen,
    setIsMenuOpen,
    setActionPopoverContent,
    actionPopoverContent,
    isUploading: props.isUploading,
  };

  return (
    <AssetCardContext.Provider value={contextValue}>
      <Popover.Root
        isOpen={isPopoverOpen}
        onOpenChange={handlePopoverOpenChange}
      >
        {props.children}
      </Popover.Root>
    </AssetCardContext.Provider>
  );
};

const AssetCardAnchor: React.FC<
  React.PropsWithChildren<Pick<V1AssetCardProps, AssetCardAnchorProps>>
> = ({ children, shouldAllowDrag = true, onClick }) => {
  const {
    anchorRef,
    handlePopoverOpenChange,
    asset,
    isMenuOpen,
    actionPopoverContent,
    isUploading,
  } = useAssetCardContext();

  const { currentDragIdentifier } = useCurrentDragType();
  const isDragging = currentDragIdentifier !== null;

  return (
    <Popover.Anchor
      ref={anchorRef}
      className={twMerge(
        "border-0.5 border-border rounded-md p-1.5 flex flex-col gap-1",
        onClick && "cursor-pointer",
      )}
      onMouseEnter={() => {
        if (
          !isDragging &&
          !isMenuOpen &&
          actionPopoverContent === null &&
          !isUploading
        ) {
          handlePopoverOpenChange(true);
        }
      }}
      onMouseLeave={() => {
        handlePopoverOpenChange(false);
      }}
      onClick={onClick}
    >
      <AssetCardDragOverlay
        asset={{
          id: asset.id,
          type: asset.type,
          url: asset.url,
          altText: asset.altText ?? "",
        }}
        shouldAllowDrag={shouldAllowDrag && !isUploading}
      >
        <AssetThumbnail asset={asset} isUploading={isUploading} />
      </AssetCardDragOverlay>
      {children}
    </Popover.Anchor>
  );
};

const AssetCardContent: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ children, className }) => {
  const { asset, isAssetPreviewLoaded, setIsAssetPreviewLoaded } =
    useAssetCardContext();

  return (
    <Popover.Content
      onOpenAutoFocus={(event) => {
        event.preventDefault();
      }}
      shouldPreventDefaultOnInteractOutside
      hideCloseButton
      side="right"
      align="start"
      sideOffset={4}
      avoidCollisions
      collisionPadding={30}
      className={twMerge(
        "pointer-events-none isolation-auto flex flex-col gap-1 rounded-md opacity-0 w-[150px] md:w-[350px] lg:w-[450px]",
        // NOTE (Gabe 2025-03-03): We use this trick to prevent layout shift
        // since the popover dimensions are based on the loaded image/video.
        isAssetPreviewLoaded && "opacity-100",
        className,
      )}
    >
      <AssetPreview
        asset={asset}
        onLoad={() => setIsAssetPreviewLoaded(true)}
      />
      {children}
    </Popover.Content>
  );
};

const AssetCardAnchorFooter: React.FC<{
  shouldAllowActions?: boolean;
}> = ({ shouldAllowActions = true }) => {
  const {
    asset,
    isMenuOpen,
    setIsMenuOpen,
    setActionPopoverContent,
    actionPopoverContent,
    isUploading,
  } = useAssetCardContext();

  const displayName = formatAssetDisplayName(asset.name);

  return (
    <div className="relative flex min-h-6 items-center justify-between gap-1">
      <span className="block truncate text-xs font-medium">
        {displayName}
        {asset.fileExtension ? `.${asset.fileExtension}` : ""}
      </span>
      {shouldAllowActions && !isUploading && (
        <AssetCardActions
          asset={asset}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          setActionPopoverContent={setActionPopoverContent}
          actionPopoverContent={actionPopoverContent}
        />
      )}
    </div>
  );
};

export const V1AssetCard = (props: V1AssetCardProps) => {
  return (
    <AssetCardRoot {...props}>
      <AssetCardAnchor
        className={twMerge(props.className, "p-0.5")}
        shouldAllowDrag={props.shouldAllowDrag}
        onClick={props.onClick}
      />
      <AssetCardContent className="p-0.5" />
    </AssetCardRoot>
  );
};

V1AssetCard.Root = AssetCardRoot;
V1AssetCard.Anchor = AssetCardAnchor;
V1AssetCard.Content = AssetCardContent;
V1AssetCard.AnchorFooter = AssetCardAnchorFooter;
